import { useState, useEffect, useMemo } from "react";
import { Loader2, Trash2, Filter, X, Check, AlertTriangle, MapPin, PencilLine, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatDuration } from "@/lib/speech";
import { renderSnippet } from "@/lib/searchSnippet";
import { ANY_LOCATION, LOCATION_FILTER_HINT, locationOptions, matchesLocation } from "@/lib/locationFilter";
import { matchesScope } from "@/lib/scope";
import { ScopeChip, ScopeFilterToggle } from "@/components/ScopeChip";
import { useCategories } from "@/context/CategoriesContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ALL } from "./shared/constants";
import { formatDate, notifyAdminBadgesDirty } from "./shared/format";
import { LoadingRow, EmptyState } from "./shared/ui";

// -------- F4: Voorstellen-review (admin approves memo-text improvements) --------
function ProposalsReview() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/proposals");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const decide = async (p, action) => {
    setBusyId(p.id);
    try {
      await api.post(`/admin/proposals/${p.id}/${action}`);
      setItems((prev) => prev.filter((x) => x.id !== p.id));
      toast.success(action === "approve" ? "Voorstel goedgekeurd — de tekst is bijgewerkt." : "Voorstel afgewezen.");
      notifyAdminBadgesDirty();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  if (loading || items.length === 0) return null; // nothing to review -> stay out of the way

  return (
    <div className="mb-6 border border-tw-yellow/30 bg-tw-yellow/5 p-4" data-testid="admin-proposals-review">
      <div className="font-mono text-[10px] tracking-[0.3em] text-tw-yellow uppercase flex items-center gap-1.5 mb-1">
        <PencilLine size={12} strokeWidth={2.5} /> Voorstellen
        <span data-testid="admin-proposals-count" className="text-white">({items.length})</span>
      </div>
      <p className="text-[11px] text-tw-muted leading-relaxed mb-3">
        Voorgestelde tekstverbeteringen. Bij goedkeuren wordt de getoonde tekst bijgewerkt — het originele transcript blijft altijd bewaard.
      </p>
      <div className="space-y-3">
        {items.map((p) => (
          <article key={p.id} data-testid={`admin-proposal-${p.id}`} className="bg-tw-surface border border-tw p-3">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="text-white font-medium text-sm">{p.memo_title}</span>
              {p.memo_category && <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-tw-muted">{p.memo_category}</span>}
              <span className="font-mono text-[10px] text-tw-muted">· voorgesteld door {p.proposer_name}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <div className="font-mono text-[9px] tracking-[0.3em] text-tw-muted uppercase mb-1">Huidig</div>
                <p className="text-tw-muted text-xs leading-relaxed whitespace-pre-wrap border-l-2 border-tw pl-2" data-testid={`admin-proposal-current-${p.id}`}>{p.current_text}</p>
              </div>
              <div>
                <div className="font-mono text-[9px] tracking-[0.3em] text-tw-yellow uppercase mb-1">Voorstel</div>
                <p className="text-white text-xs leading-relaxed whitespace-pre-wrap border-l-2 border-tw-yellow pl-2" data-testid={`admin-proposal-proposed-${p.id}`}>{p.proposed_text}</p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => decide(p, "reject")} disabled={busyId === p.id} data-testid={`admin-proposal-reject-${p.id}`}
                className="tw-press inline-flex items-center gap-1.5 px-3 h-9 border border-tw text-tw-muted hover:text-tw-red hover:border-tw-red font-mono text-[10px] uppercase tracking-[0.15em] disabled:opacity-50">
                {busyId === p.id ? <Loader2 size={12} className="animate-spin" /> : <X size={13} strokeWidth={2.8} />} Afwijzen
              </button>
              <button type="button" onClick={() => decide(p, "approve")} disabled={busyId === p.id} data-testid={`admin-proposal-approve-${p.id}`}
                className="tw-press inline-flex items-center gap-1.5 px-3 h-9 bg-tw-yellow text-black font-bold uppercase tracking-[0.15em] text-[10px] disabled:opacity-50">
                {busyId === p.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={13} strokeWidth={3} />} Goedkeuren
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

export function RecordingsTab({ search, setSearch, searching, category, setCategory, searchLoading, searchResults, handleDelete, deletingId, filtersActive, clearFilters, users, userId, setUserId, loading, items }) {
  const { categories: categoryList, colorOf } = useCategories();
  // Round I — locatiefilter (client-side op de al geladen lijst).
  const [locationFilter, setLocationFilter] = useState(null);
  const locationChoices = useMemo(() => locationOptions(items), [items]);
  // Round J — the list lives in the Admin shell; a toggled scope is kept here as an override.
  const [onlyAll, setOnlyAll] = useState(false);
  const [scopeOverrides, setScopeOverrides] = useState({});
  const visibleItems = useMemo(
    () => items
      .map((r) => (scopeOverrides[r.id] ? { ...r, ...scopeOverrides[r.id] } : r))
      .filter((r) => matchesLocation(r, locationFilter) && matchesScope(r, onlyAll)),
    [items, locationFilter, onlyAll, scopeOverrides],
  );
  return (
    <>
          <ProposalsReview />

          {/* B3.2 — search across ALL memos */}
          <div className="relative mb-4">
            <Search size={14} strokeWidth={2.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-tw-muted pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Zoek in alle memo's…"
              data-testid="admin-search"
              className="pl-9 pr-16 bg-tw-surface border-tw text-white h-11 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-tw-yellow"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                data-testid="admin-search-clear"
                className="tw-press absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-tw-muted hover:text-tw-yellow"
              >
                Wis
              </button>
            )}
          </div>

          {searching ? (
            <>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">ZOEKRESULTATEN</div>
                  {category !== ALL && (
                    <button
                      type="button"
                      onClick={() => setCategory(ALL)}
                      data-testid="admin-search-category-chip"
                      className="tw-press inline-flex items-center gap-1.5 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-black bg-tw-yellow hover:bg-[#FFDE33]"
                    >
                      in categorie {category} <X size={11} strokeWidth={2.8} />
                    </button>
                  )}
                </div>
                <div className="font-mono text-sm text-tw-yellow" data-testid="admin-search-count">
                  {searchLoading ? "…" : (searchResults || []).length}
                </div>
              </div>
              {searchLoading ? (
                <LoadingRow />
              ) : (searchResults || []).length === 0 ? (
                <EmptyState label={`Geen resultaten voor "${search.trim()}"`} hint="Pas je zoekterm of categorie aan." testId="admin-search-empty" />
              ) : (
                <div className="space-y-2" data-testid="admin-search-results">
                  {searchResults.map((r, i) => (
                    <article
                      key={r.id}
                      className="tw-fadeup bg-tw-surface border border-tw p-5 hover:border-tw-yellow transition-colors duration-150"
                      style={{ animationDelay: `${Math.min(i * 30, 400)}ms` }}
                      data-testid={`admin-search-result-${r.id}`}
                    >
                      <header className="flex flex-wrap items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span
                            className="inline-block px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-black"
                            style={{ backgroundColor: colorOf(r.category) }}
                          >
                            {r.category}
                          </span>
                          {r.user_name && <span className="text-white text-sm font-medium">{r.user_name}</span>}
                          <span className="font-mono text-[11px] text-tw-muted">· {formatDate(r.created_at)}</span>
                        </div>
                        <button
                          onClick={() => handleDelete(r.id)}
                          disabled={deletingId === r.id}
                          data-testid={`admin-search-delete-${r.id}`}
                          className="text-tw-muted hover:text-tw-red p-1 transition-colors duration-150"
                          aria-label="Delete recording"
                        >
                          {deletingId === r.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                        </button>
                      </header>
                      <h3 className="font-display text-lg font-bold text-white leading-snug tracking-tight mb-1.5">{r.title}</h3>
                      <p className="text-tw-muted text-sm leading-relaxed">{renderSnippet(r.snippet)}</p>
                    </article>
                  ))}
                </div>
              )}
            </>
          ) : (
          <>
          {/* Filters */}
          <div className="bg-tw-surface border border-tw p-4 sm:p-5 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Filter size={14} className="text-tw-yellow" />
              <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">FILTERS</div>
              {filtersActive && (
                <button
                  onClick={clearFilters}
                  data-testid="admin-clear-filters"
                  className="ml-auto flex items-center gap-1 text-[11px] uppercase tracking-widest text-tw-muted hover:text-white transition-colors"
                >
                  <X size={12} /> Clear
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger
                  data-testid="admin-user-filter"
                  className="bg-tw-black border-tw text-white h-11 rounded-none focus:ring-0 focus:ring-offset-0 focus:border-tw-yellow"
                >
                <SelectValue placeholder="Alle gebruikers" />
                </SelectTrigger>
                <SelectContent className="bg-tw-surface border-tw text-white rounded-none max-h-72">
                  <SelectItem value={ALL} className="focus:bg-tw-yellow focus:text-black rounded-none">
                    Alle gebruikers
                  </SelectItem>
                  {users.map((u) => (
                    <SelectItem
                      key={u.id}
                      value={u.id}
                      data-testid={`admin-user-option-${u.email}`}
                      className="focus:bg-tw-yellow focus:text-black rounded-none"
                    >
                      {u.name} {u.role === "admin" && "· admin"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger
                  data-testid="admin-category-filter"
                  className="bg-tw-black border-tw text-white h-11 rounded-none focus:ring-0 focus:ring-offset-0 focus:border-tw-yellow"
                >
                <SelectValue placeholder="Alle categorieën" />
                </SelectTrigger>
                <SelectContent className="bg-tw-surface border-tw text-white rounded-none">
                  <SelectItem value={ALL} className="focus:bg-tw-yellow focus:text-black rounded-none">
                    Alle categorieën
                  </SelectItem>
                  {categoryList.map((c) => (
                    <SelectItem
                      key={c.name}
                      value={c.name}
                      data-testid={`admin-category-option-${c.name}`}
                      className="focus:bg-tw-yellow focus:text-black rounded-none uppercase tracking-wider"
                    >
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={locationFilter || ANY_LOCATION} onValueChange={(v) => setLocationFilter(v === ANY_LOCATION ? null : v)}>
                <SelectTrigger
                  data-testid="admin-location-filter"
                  className="bg-tw-black border-tw text-white h-11 rounded-none focus:ring-0 focus:ring-offset-0 focus:border-tw-yellow"
                >
                  <SelectValue placeholder="Alle locaties" />
                </SelectTrigger>
                <SelectContent className="bg-tw-surface border-tw text-white rounded-none">
                  <SelectItem value={ANY_LOCATION} className="focus:bg-tw-yellow focus:text-black rounded-none">
                    Alle locaties
                  </SelectItem>
                  {locationChoices.map((loc) => (
                    <SelectItem
                      key={loc}
                      value={loc}
                      data-testid={`admin-location-option-${loc}`}
                      className="focus:bg-tw-yellow focus:text-black rounded-none uppercase tracking-wider"
                    >
                      {loc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <ScopeFilterToggle active={onlyAll} onToggle={() => setOnlyAll((v) => !v)} testId="admin-scope-filter" />
              <p className="text-[11px] text-tw-muted" data-testid="admin-location-hint">{LOCATION_FILTER_HINT}</p>
            </div>
          </div>

          <div className="flex items-center justify-between mb-4">
            <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted">RESULTATEN</div>
            <div className="font-mono text-sm text-tw-yellow" data-testid="admin-results-count">
              {visibleItems.length}
            </div>
          </div>

          {loading ? (
            <LoadingRow />
          ) : visibleItems.length === 0 ? (
            <EmptyState label="Geen memo's gevonden" hint="Wis de filters om alles te zien." testId="admin-empty" />
          ) : (
            <div className="space-y-2">
              {visibleItems.map((r, i) => (
                <article
                  key={r.id}
                  className="tw-fadeup bg-tw-surface border border-tw p-5 hover:border-tw-yellow transition-colors duration-150"
                  style={{ animationDelay: `${Math.min(i * 30, 400)}ms` }}
                  data-testid={`admin-recording-${r.id}`}
                >
                  <header className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span
                        className="inline-block px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-black"
                        style={{ backgroundColor: colorOf(r.category) }}
                      >
                        {r.category}
                      </span>
                      <span className="text-white text-sm font-medium">{r.user_name}</span>
                      {r.user_location && (
                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-tw-muted border border-tw px-1.5 py-0.5 inline-flex items-center gap-1" data-testid={`admin-speaker-location-${r.id}`} title="Locatie waar deze memo bij hoort">
                          <MapPin size={9} strokeWidth={2.5} /> hoort bij {r.user_location}
                        </span>
                      )}
                      <ScopeChip item={r} canEdit onChange={(s) => setScopeOverrides((o) => ({ ...o, [r.id]: s }))} testId={`admin-scope-${r.id}`} />
                      <span className="font-mono text-[11px] text-tw-muted">{r.user_email}</span>
                      <span className="font-mono text-[11px] text-tw-muted">· {formatDate(r.created_at)}</span>
                      {typeof r.duration_seconds === "number" && (
                        <span className="font-mono text-[11px] text-tw-muted">· {formatDuration(r.duration_seconds)}</span>
                      )}
                    </div>
                    <button
                      onClick={() => handleDelete(r.id)}
                      disabled={deletingId === r.id}
                      data-testid={`admin-delete-${r.id}`}
                      className="text-tw-muted hover:text-tw-red p-1 transition-colors duration-150"
                      aria-label="Delete recording"
                    >
                      {deletingId === r.id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </button>
                  </header>
                  {r.auto_title && (
                    <h3 className="font-display text-lg font-bold text-white leading-snug tracking-tight mb-2" data-testid={`admin-recording-title-${r.id}`}>
                      {r.auto_title}
                    </h3>
                  )}
                  {((r.tags && r.tags.length > 0) || (r.locations && r.locations.length > 0)) && (
                    <div className="mb-3 flex flex-wrap gap-1.5" data-testid={`admin-recording-chips-${r.id}`}>
                      {(r.locations || []).map((loc) => (
                        <span
                          key={`loc-${loc}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-tw-yellow border border-tw-yellow/40 bg-tw-yellow/5"
                        >
                          <MapPin size={10} strokeWidth={2.5} /> {loc}
                        </span>
                      ))}
                      {(r.tags || []).map((t) => (
                        <span
                          key={`tag-${t}`}
                          className="inline-block px-2 py-0.5 font-mono text-[10px] tracking-[0.1em] text-tw-muted border border-tw bg-tw-black"
                        >
                          #{t}
                        </span>
                      ))}
                    </div>
                  )}
                  {r.question && (
                    <div className="mb-3 -mt-1" data-testid={`admin-recording-question-${r.id}`}>
                      <div className="font-mono text-[9px] tracking-[0.3em] text-tw-yellow mb-1">
                        KENNISVRAAG
                      </div>
                      <p className="text-tw-muted text-sm italic leading-snug">&ldquo;{r.question}&rdquo;</p>
                    </div>
                  )}
                  {r.auto_summary && (
                    <div className="mb-3" data-testid={`admin-recording-summary-${r.id}`}>
                      <div className="font-mono text-[9px] tracking-[0.3em] text-tw-yellow mb-1">
                        SAMENVATTING (AI)
                      </div>
                      <p className="text-white text-sm leading-relaxed">{r.auto_summary}</p>
                    </div>
                  )}
                  {r.display_text && (
                    <div className="mb-1.5 inline-flex items-center gap-1 border border-tw-yellow/60 text-tw-yellow px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em]" data-testid={`admin-recording-corrected-${r.id}`}>
                      <PencilLine size={9} strokeWidth={2.8} /> gecorrigeerd
                    </div>
                  )}
                  {r.transcript_warning && (
                    <div
                      className="mb-2 flex items-center gap-1.5 border border-tw-yellow bg-tw-yellow/10 px-2 py-1 font-mono text-[10px] tracking-[0.1em] text-tw-yellow"
                      data-testid={`admin-recording-transcript-warning-${r.id}`}
                    >
                      <AlertTriangle size={12} strokeWidth={2.5} />
                      Let op: deze opname bevatte veel stilte — controleer het transcript.
                    </div>
                  )}
                  <p className="text-white leading-relaxed whitespace-pre-wrap">{r.display_text || r.transcript}</p>
                </article>
              ))}
            </div>
          )}
          </>
          )}
    </>
  );
}
